import { Box, Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import {
  Add,
  Button,
  Chip,
  DatePicker,
  DottedText,
  Download,
  PageTitleAndSubTitle,
  RadioButton,
  TiltedTopArrow,
  TypableDropdown,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import RiskRegisterConstants from '../../../core-utils/messages/RiskRegisterConstants';
import { CheckBox } from '@HomaHealth/ui-component-library';
import { formatDateToOrdinal } from '../../../core-utils/helper-utils/helper';
import {
  getMitigationChips,
  getPlannedInfoMessages,
  getRiskIdChip,
  getSeverityIcon,
  removeEmptyFields,
} from '../../../core-utils/helper-utils/riskRegisterHelper';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import moment from 'moment';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import { getAvatarComponent } from '../../../core-utils/helper-utils/TaskBoardHelper';

export interface RiskData {
  _id: string;
  risk_id: string;
  eg_nid: string;
  title: string;
  description: string;
  date_identified: string;
  impact: any[];
  risk_source: string;
  severity_level: string;
  risk_status: string;
  files: File[];
  start_date?: string;
  activity: any[];
  planned_quarter: string;
  delay_reason: string[];
  acceptance_reason: string[];
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  source_report?: string;
  mitigation_activity?: any[];
  selected_acceptance_reason?: string;
  selected_delay_reason?: string;
  contract_id?: any;
  compare_agreement_id?: any;
  vendor_type?: any;
}

export interface AcknowledgeRiskModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
   
  handleSubmit: (data: any) => void;
  handleOpenSnackBar: any;
  risk: RiskData;
  eg_nid: string;
  handleDownload?: () => void;
  handleNavigate?: any;
}

const AcknowledgeRiskModal: React.FC<AcknowledgeRiskModalProps> = observer(
  ({
    open,
    setOpen,
    handleSubmit,
    risk,
    eg_nid,
    handleOpenSnackBar,
    handleDownload,
    handleNavigate,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '850px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
      },
      activity: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 0 0px 20px',
      },
      infoItem: {
        boxSizing: 'border-box',
        height: '100px',
        width: '764px',
        background: patientAccessTheme.palette.grey[100],
        border: '0px',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '20px',
      },
    });
    const { classes } = useStyles();
    //Constants
    const messages = RiskRegisterConstants;

    //States
    const [riskDecision, setRiskDecision] = useState<string>(
      risk?.risk_status !== 'New' ? 'mitigate' : '',
    );
    const [data, setData] = useState<any>({
      risk_id: risk._id,
      status: risk.risk_status,
      files: [],
      activity:
        risk.activity[0] !== 'NA'
          ? risk?.activity
          : [
              {
                activity_id: '',
                start_date: '',
                name: '',
                owner: [],
                tracker: '',
              },
            ],
      planned_quarter: risk?.planned_quarter ?? '',
      selected_acceptance_reason: risk?.selected_acceptance_reason ?? '',
      selected_delay_reason: risk?.selected_delay_reason ?? '',
    });
    const [acceptanceReasons, setAcceptanceReasons] = useState<any>([]);
    const [delayReasons, setDelayReasons] = useState<any>([]);
    const [selectedChip, setSelectedChip] = useState<string | null>(
      risk?.planned_quarter !== '' ? risk?.planned_quarter : null,
    );
    const [createMitigation, setCreateMitigation] = useState<boolean>(false);
    const [ownerOptions, setOwnerOptions] = useState<any>([]);
    const [trackerOptions, setTrackerOptions] = useState<any>([]);
    const [trackerValues, setTrackerValues] = useState<any>([]);

    const isAccepted = riskDecision === 'accept';
    // const isAcceptanceReasonFilled = !!data.selected_acceptance_reason;
    const isActivityFieldsFilled = data.activity.some(
      (item: any) =>
        item.name && item.start_date && item.owner.length > 0 && item.tracker,
    );
    const isMitigateAndCurrentQuarter =
      riskDecision === 'mitigate' && selectedChip === 'Current Quarter';
    const isMitigateAndFutureQuarter =
      riskDecision === 'mitigate' &&
      selectedChip !== 'Current Quarter' &&
      !createMitigation;
    const isPlannedWithCreateMitigation =
      riskDecision === 'mitigate' && createMitigation;
    const isButtonDisabled = !(
      (isMitigateAndCurrentQuarter && isActivityFieldsFilled) ||
      (isMitigateAndFutureQuarter && !createMitigation) ||
      (isPlannedWithCreateMitigation && isActivityFieldsFilled) ||
      isAccepted
    );

    const {
      plannedInfoTitle,
      plannedInfoSub,
      plannedBackground,
      plannedTextColor,
    } = getPlannedInfoMessages(selectedChip);

    //functions
    const handleClose = () => {
      setOpen(false);
    };

    const handleChange = (event: any, fieldName: string) => {
      const value = event.target.value as string;
      setData((prevData: any) => ({
        ...prevData,
        [fieldName.toLowerCase()]: value,
      }));
    };

    const handleRiskDecisionChange = async (decision: string) => {
      setRiskDecision(decision);
      setData((prevData: any) => ({
        ...prevData,
        activity:
          risk.activity[0] !== 'NA'
            ? risk?.activity
            : [
                {
                  activity_id: '',
                  start_date: '',
                  name: '',
                  owner: [],
                  tracker: '',
                },
              ],
        planned_quarter: risk?.planned_quarter ?? '',
        selected_acceptance_reason: risk?.selected_acceptance_reason ?? '',
        selected_delay_reason: risk?.selected_delay_reason ?? '',
      }));
    };

    //Apis
    const getAcceptanceReasons = async () => {
      const acceptanceReasons = await axios.post(
        properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.getAcceptanceReason,
        { source_report: risk.source_report },
      );
      setAcceptanceReasons(acceptanceReasons.data.data);
    };

    const getDelayReasons = async () => {
      const delayReasons = await axios.post(
        properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.getDelayReason,
        { source_report: data.source_report },
      );
      setDelayReasons(delayReasons.data.data);
    };

    const getContracts = async () => {
      const contracts = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getContracts,
        {
          eg_nid: risk?.eg_nid,
        },
      );
      const ownerOptions = contracts.data.data.map((item: any) => {
        return `${item.service_agreement_name} - ${item.health_care_partner_name}`;
      });
      setOwnerOptions(ownerOptions);
      // setOwnerOptions(['ll', 'oo', 'uu']);
    };

    const getTrackersList = async () => {
      const roles = ['ADMIN', 'USER'];
      const requests = roles.map((role) =>
        axios.get(
          `${properties.userManagementBaseUrl}${properties.users}${properties.getByRole}/${role}`,
        ),
      );

      const responses = await Promise.all(requests);

      const allUsers = responses
        .flatMap((res) => res.data)
        .filter((user: any) =>
          user.employerGroups?.some((group: any) => group.egNid === eg_nid),
        );
      const options = allUsers.map((item: any) =>
        getAvatarComponent(item.email, item.firstName, item.lastName),
      );
      const values = allUsers.map((item: any) => item.email);
      setTrackerOptions(options);
      setTrackerValues(values);
      // setTrackerOptions(['ii', 'jj']);
      // setTrackerValues(['ii', 'jj']);
    };

    useEffect(() => {
      getAcceptanceReasons();
      getDelayReasons();
      getContracts();
      getTrackersList();
    }, []);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
              }}
            >
              <PageTitleAndSubTitle
                title={messages.ACKNOWLEDGE_RISK.TITLE}
                subTitle={messages.ACKNOWLEDGE_RISK.sUBTITLE}
                subTitleStyles={{ width: '760px' }}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid
              className={classes.scroll}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                overflowY: 'scroll',
                height: '555px',
                padding: '20px 20px 20px 0px',
              }}
            >
              <Grid className={classes.infoItem} style={{ height: '200px' }}>
                <PageTitleAndSubTitle
                  title={
                    <Typography
                      fontSize="18px"
                      lineHeight="22px"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      fontType="semiBold"
                      style={{ width: risk?.source_report ? '60%' : '100%' }}
                    >
                      {risk?.title}
                    </Typography>
                  }
                  subTitle={risk?.description}
                  topRightItem={
                    <div className={classes.flex}>
                      {risk.source_report && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                          onClick={handleDownload}
                        >
                          <Download
                            style={{
                              width: '15px',
                              height: '15px',
                              stroke: patientAccessTheme.palette.primary[500],
                            }}
                          />
                          <DottedText
                            typographyStyles={{
                              fontSize: '14px',
                              lineHeight: '24px',
                              fontWeight: 500,
                              color: patientAccessTheme.palette.primary[500],
                            }}
                            text={messages.ACKNOWLEDGE_RISK.DOWNLOAD_REPORT}
                          />
                        </div>
                      )}

                      <Typography
                        fontSize="14px"
                        lineHeight="22px"
                        textColor={
                          patientAccessTheme.palette.text.mediumemphasis
                        }
                      >
                        {risk?.date_identified
                          ? formatDateToOrdinal(risk?.date_identified)
                          : ''}
                      </Typography>
                    </div>
                  }
                  subTitleStyles={{ width: '724px' }}
                />
                <Grid
                  className={classes.flex}
                  style={{ gap: '30px', marginTop: '25px' }}
                >
                  {getSeverityIcon(risk?.severity_level)}
                  {getRiskIdChip(risk.risk_id)}{' '}
                  <div className={classes.flex}>
                    <Typography
                      fontSize="14px"
                      lineHeight="22px"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                    >
                      Source:
                    </Typography>
                    <div
                      className={classes.flex}
                      style={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        handleNavigate(
                          risk?.contract_id,
                          risk?.source_report,
                          risk?.compare_agreement_id,
                          handleOpenSnackBar,
                        );
                      }}
                    >
                      <DottedText
                        typographyStyles={{
                          fontSize: '14px',
                          lineHeight: '24px',
                          fontWeight: 500,
                          color: patientAccessTheme.palette.primary[500],
                        }}
                        text={risk?.source_report ?? risk?.risk_source}
                      />
                      <TiltedTopArrow
                        style={{
                          width: '10px',
                          height: '11px',
                          stroke: patientAccessTheme.palette.primary[500],
                        }}
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
              <Grid style={{ marginTop: '20px' }}>
                <Typography
                  fontSize="16px"
                  lineHeight="22px"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {messages.ACKNOWLEDGE_RISK.RISK_DECISION}
                </Typography>
                <Grid
                  className={classes.flex}
                  style={{ marginTop: '10px', gap: '20px' }}
                >
                  <Grid
                    className={classes.flex}
                    style={{
                      width: '370px',
                      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
                      padding: '5x',
                      borderRadius: '8px',
                    }}
                  >
                    <RadioButton
                      color={patientAccessTheme.palette.primary[500]}
                      checked={riskDecision === 'mitigate'}
                      handleChange={() => handleRiskDecisionChange('mitigate')}
                    />
                    <Typography
                      fontSize="14px"
                      fontType="medium"
                      lineHeight="22px"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                    >
                      {messages.ACKNOWLEDGE_RISK.MITIGATE}
                    </Typography>
                  </Grid>
                  <Grid
                    className={classes.flex}
                    style={{
                      width: '370px',
                      padding: '5px',
                      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
                      borderRadius: '8px',
                    }}
                  >
                    <RadioButton
                      color={patientAccessTheme.palette.primary[500]}
                      checked={riskDecision === 'accept'}
                      handleChange={() => handleRiskDecisionChange('accept')}
                    />
                    <Typography
                      fontSize="14px"
                      fontType="medium"
                      lineHeight="22px"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                    >
                      {messages.ACKNOWLEDGE_RISK.ACCEPT}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {riskDecision === 'accept' && (
                <>
                  <Grid
                    className={classes.infoItem}
                    style={{ background: patientAccessTheme.palette.grey[100] }}
                  >
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                      <Info
                        height={'16px'}
                        width="16px"
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        fontSize="14px"
                        lineHeight="177%"
                        fontType="semiBold"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {messages.MAIN_PAGE.ACCEPTED_INFO_TITLE}
                      </Typography>
                    </Grid>
                    <Typography
                      fontSize="14px"
                      lineHeight="177%"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                    >
                      {messages.MAIN_PAGE.ACCEPTANCE_INFO_SUB}
                    </Typography>
                  </Grid>
                  <ReportCreateNewLabelField
                    label={messages.MAIN_PAGE.ACCEPTANCE_REASON}
                    component={
                      <TypableDropdown
                        placeholder={messages.MAIN_PAGE.ACCEPTANCE_REASON}
                        dropDownOptions={
                          risk?.acceptance_reason?.length > 0
                            ? risk.acceptance_reason.map((acc: any) => acc)
                            : acceptanceReasons.map(
                                (acc: any) => acc.description,
                              )
                        }
                        dropDownValues={
                          risk?.acceptance_reason?.length > 0
                            ? risk.acceptance_reason.map((acc: any) => acc)
                            : acceptanceReasons.map(
                                (acc: any) => acc.description,
                              )
                        }
                        value={data.selected_acceptance_reason}
                        textFieldPlaceholder="Enter reason for acceptance here (or) select from below"
                        handleInputChange={(value: string) =>
                          setData((prevData: any) => ({
                            ...prevData,
                            selected_acceptance_reason: value,
                          }))
                        }
                        handleChange={(event: any) =>
                          handleChange(event, 'selected_acceptance_reason')
                        }
                        height="36px"
                        width="723px"
                        // error={!data.selected_acceptance_reason}
                      />
                    }
                  />
                </>
              )}
              {riskDecision === 'mitigate' && (
                <Grid
                  style={{
                    display: 'flex',
                    gap: '10px',
                    flexDirection: 'column',
                    alignItems: 'start',
                  }}
                >
                  <Typography
                    fontSize="16px"
                    lineHeight="22px"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    {messages.ACKNOWLEDGE_RISK.PLAN_MITIGATION}
                  </Typography>
                  <Typography
                    fontSize="12px"
                    lineHeight="22px"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    {messages.ACKNOWLEDGE_RISK.START_FROM}
                  </Typography>
                  <Grid
                    className={classes.flex}
                    style={{ flexDirection: 'row' }}
                  >
                    {getMitigationChips().map((item: string) => {
                      const isSelected = selectedChip === item;
                      const textColor = isSelected
                        ? patientAccessTheme.palette.primary[500]
                        : patientAccessTheme.palette.text.highemphasis;

                      return (
                        <Chip
                          key={item}
                          label={
                            <Typography
                              fontSize="12px"
                              fontType="medium"
                              lineHeight="21.24px"
                              textColor={textColor}
                            >
                              {item}
                            </Typography>
                          }
                          backgroundColor={
                            isSelected
                              ? patientAccessTheme.palette.primary[100]
                              : patientAccessTheme.palette.status.white
                          }
                          border={
                            isSelected
                              ? `1px solid ${patientAccessTheme.palette.primary[500]}`
                              : `1px solid ${patientAccessTheme.palette.grey[300]}`
                          }
                          onClick={() => {
                            setSelectedChip(item);
                            setData((prevData: any) => ({
                              ...prevData,
                              planned_quarter: item,
                            }));
                          }}
                        />
                      );
                    })}
                  </Grid>
                  {selectedChip !== null && (
                    <>
                      <Grid
                        className={classes.infoItem}
                        style={{ backgroundColor: plannedBackground }}
                      >
                        <Grid style={{ display: 'flex', alignItems: 'center' }}>
                          <Info
                            height={'16px'}
                            width="16px"
                            style={{ marginRight: '8px' }}
                            stroke={plannedTextColor}
                          />
                          <Typography
                            fontSize="14px"
                            lineHeight="177%"
                            fontType="semiBold"
                            textColor={plannedTextColor}
                          >
                            {plannedInfoTitle}
                          </Typography>
                        </Grid>
                        <Typography
                          fontSize="14px"
                          lineHeight="177%"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                        >
                          {plannedInfoSub}
                        </Typography>
                      </Grid>
                      {selectedChip !== 'Current Quarter' && (
                        <>
                          <ReportCreateNewLabelField
                            label={messages.MAIN_PAGE.DELAY_REASON}
                            component={
                              <TypableDropdown
                                placeholder={messages.MAIN_PAGE.DELAY_REASON}
                                dropDownOptions={
                                  risk?.delay_reason?.length > 0
                                    ? risk.delay_reason.map((acc: any) => acc)
                                    : delayReasons.map(
                                        (acc: any) => acc.description,
                                      )
                                }
                                dropDownValues={
                                  risk?.delay_reason?.length > 0
                                    ? risk.delay_reason.map((acc: any) => acc)
                                    : delayReasons.map(
                                        (acc: any) => acc.description,
                                      )
                                }
                                value={data.selected_delay_reason}
                                textFieldPlaceholder="Enter reason for delay here (or) select from below"
                                height="36px"
                                width="723px"
                                handleChange={(event: any) =>
                                  handleChange(event, 'selected_delay_reason')
                                }
                                handleInputChange={(value) => {
                                  setData((prev: any) => ({
                                    ...prev,
                                    selected_delay_reason: value,
                                  }));
                                }}
                                // error={!data.selected_delay_reason}
                              />
                            }
                          />
                          <Grid
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <CheckBox
                              checked={createMitigation}
                              id=""
                              name=""
                              color="primary"
                              handleChange={() =>
                                setCreateMitigation(!createMitigation)
                              }
                            />
                            <Typography
                              fontSize="12px"
                              lineHeight="22px"
                              textColor={
                                patientAccessTheme.palette.text.highemphasis
                              }
                            >
                              {messages.MAIN_PAGE.FUTURE_MITIGATION}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              )}
              {(selectedChip === 'Current Quarter' || createMitigation) &&
                riskDecision === 'mitigate' &&
                data.activity.map((act: any, index: number) => (
                  <>
                    <Grid
                      key={index}
                      className={`${classes.flex} ${classes.activity}`}
                      style={{ flexDirection: 'column', alignItems: 'start' }}
                    >
                      <ReportCreateNewLabelField
                        label={messages.MAIN_PAGE.START_DATE}
                        component={
                          <DatePicker
                            width="355px"
                            handleChange={(value) => {
                              const newActivities = [...data.activity];
                              newActivities[index].start_date = moment(
                                value,
                              ).format('MMMM D, YYYY');
                              setData((prev: any) => ({
                                ...prev,
                                activity: newActivities,
                              }));
                            }}
                            selectedDate={
                              act.start_date ? moment(act.start_date) : null
                            }
                            minDate={'Jan 01, 2020'}
                            maxDate={moment().add(1, 'years')}
                            format={'MMMM d, yyyy'}
                            placeholder={messages.MAIN_PAGE.DATE_FORMAT}
                            error={!act.start_date}
                          />
                        }
                      />
                      <ReportCreateNewLabelField
                        label={messages.MAIN_PAGE.MITIGATION_ACTIVITY}
                        component={
                          <TypableDropdown
                            dropDownOptions={
                              risk?.mitigation_activity?.map(
                                (item: any) => item.activity,
                              ) ?? []
                            }
                            dropDownValues={
                              risk?.mitigation_activity?.map(
                                (item: any) => item.activity,
                              ) ?? []
                            }
                            placeholder={messages.MAIN_PAGE.MITIGATION_ACTIVITY}
                            value={act.name}
                            handleChange={(event) => {
                              const selectedActivity = event.target.value;
                              const matchedItem = risk?.mitigation_activity?.find(
                                (item: any) =>
                                  item.activity === selectedActivity,
                              );

                              const newActivities = [...data.activity];
                              newActivities[index].name = selectedActivity;
                              newActivities[index].activity_id =
                                matchedItem?.act_uid || '';
                              setData((prev: any) => ({
                                ...prev,
                                activity: newActivities,
                              }));
                            }}
                            handleInputChange={(value) => {
                              const newActivities = [...data.activity];
                              newActivities[index].name = value;
                              setData((prev: any) => ({
                                ...prev,
                                activity: newActivities,
                              }));
                            }}
                            textFieldPlaceholder="Enter Activity here (or) select from below"
                            height="36px"
                            width="723px"
                            error={!act.activity}
                          />
                        }
                        marginBottom="12px"
                      />
                      <Grid
                        className={classes.flex}
                        style={{ flexDirection: 'row', gap: '15px' }}
                      >
                        <ReportCreateNewLabelField
                          label={messages.MAIN_PAGE.OWNER}
                          component={
                            <AutocompleteWithCheckBoxes
                              options={ownerOptions}
                              defaultValue={act.owner}
                              handleChange={(_, value) => {
                                const newActivities = [...data.activity];
                                newActivities[index].owner = value;
                                setData((prev: any) => ({
                                  ...prev,
                                  activity: newActivities,
                                }));
                              }}
                              getOptionLabel={(option: any) => option}
                              placeholder={messages.MAIN_PAGE.OWNER}
                              width="355px"
                              renderOption={(
                                option: any,
                                { selected }: any,
                              ) => {
                                const index = ownerOptions.indexOf(option);
                                return (
                                  <Box
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <CheckBox
                                      checked={selected}
                                      id=""
                                      name=""
                                      color="primary"
                                    />
                                    {ownerOptions[index]}
                                  </Box>
                                );
                              }}
                              noOptionsText="No Owner Found"
                            />
                          }
                          marginBottom="12px"
                        />
                        <ReportCreateNewLabelField
                          label={messages.MAIN_PAGE.TRACKER}
                          component={
                            <Dropdown
                              label={messages.MAIN_PAGE.TRACKER}
                              dropDownOptions={trackerOptions}
                              dropDownValues={trackerValues}
                              value={act.tracker}
                              setValue={(value: string) => {
                                const newActivities = [...data.activity];
                                newActivities[index].tracker = value;
                                setData((prev: any) => ({
                                  ...prev,
                                  activity: newActivities,
                                }));
                              }}
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '355px',
                              }}
                              labelTop="10px"
                              onChange={(event: any) => {
                                const newActivities = [...data.activity];
                                newActivities[index].tracker =
                                  event.target.value;
                                setData((prev: any) => ({
                                  ...prev,
                                  activity: newActivities,
                                }));
                              }}
                              error={!act.tracker}
                            />
                          }
                        />
                      </Grid>
                    </Grid>
                    <Button
                      label={
                        <Typography
                          textColor={patientAccessTheme.palette.primary[500]}
                          fontSize="12px"
                          lineHeight="12px"
                          fontType={'medium'}
                        >
                          {messages.MAIN_PAGE.ADD_MITIGATION}
                        </Typography>
                      }
                      variant="outlined"
                      height="36px"
                      width="180px"
                      styleProps={{ marginRight: '25px' }}
                      borderRadius="4px"
                      startIcon={
                        <Add
                          stroke={patientAccessTheme.palette.primary[500]}
                          fill={patientAccessTheme.palette.primary[500]}
                        />
                      }
                      onClick={() => {
                        setData((prev: any) => ({
                          ...prev,
                          activity: [
                            ...prev.activity,
                            {
                              activity_id: '',
                              name: '',
                              start_date: '',
                              owner: [],
                              tracker: '',
                            },
                          ],
                        }));
                      }}
                    />
                  </>
                ))}
            </Grid>
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary[500]}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.MAIN_PAGE.CANCEL}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  setOpen(false);
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.status.white}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.ACKNOWLEDGE_RISK.SAVE_PREFERENCE}
                  </Typography>
                }
                variant="contained"
                color="primary"
                height="36px"
                width="180px"
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  const cleanedData = removeEmptyFields({
                    ...data,
                    status: riskDecision === 'accept' ? 'Accepted' : 'Planned',
                  });
                  handleSubmit({
                    ...cleanedData,
                    ...(cleanedData.planned_quarter === 'Current Quarter'
                      ? { status: 'In Progress' }
                      : {}),
                  });
                  setOpen(false);
                }}
                disabled={isButtonDisabled}
              />
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default AcknowledgeRiskModal;
